// external
import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import useSnackbarHelper from '../../hooks/snackbar.hook';

// internal
import './login.page.css';
import logo from '../../assets/coelab-logo-black.png';
import backgroundImage from '../../assets/background-image.png';
import useService from '../../hooks/service.hook';
import AuthenticationService from '../../services/authentication.service';
import { isEmailInvalid } from '../../helpers/general.helpers';
import { Alert, Card, CardContent, CardHeader, Link, TextField, Typography } from '@mui/material';
import { AppContext, User } from '../../models/user.models';
import AppContextService from '../../services/app-context.service';
import { palette } from '../../theme';
import { FieldErrors, useFieldErrors } from '../../components/form-validation';
// import { ReCAPTCHA } from 'react-google-recaptcha';

const LoginPage: FC = () => {
  type LoginField = 'email' | 'password';

  // state
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { fieldErrors, setFieldErrors, clearFieldError } = useFieldErrors<LoginField>();

  // hooks
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbarHelper();
  const [searchParams, setSearchParams] = useSearchParams();
  const authenticationService = useService(AuthenticationService);
  const appContextService = useService(AppContextService);

  useEffect(() => {
    checkSessionExpired();
  }, []);

  const checkSessionExpired = () => {
    if (searchParams.get('sessionExpired')) {
      setSearchParams();
      showSnackbar('Your session has expired. Please log back in to continue using CoeLab', 'info');
    }
  };

  const handleLogin = () => {
    setFieldErrors({});
    if (validate()) {
      const payload = {
        email,
        password,
      };
      setIsLoading(true);
      authenticationService
        .loginUser(payload)
        .then((response: any) => {
          const responseObj = response.data.data;
          const userData: User = {
            token: responseObj.token,
            firstName: responseObj.firstName,
            lastName: responseObj.lastName,
            email: responseObj.email,
            id: responseObj.userId,
            role: responseObj.role,
            expiration: responseObj.exp,
            profileImage: responseObj.profileImage,
            measurementSystem: responseObj.measurementSystem,
            birthday: responseObj.birthday,
            sex: responseObj.sex,
          };
          window.localStorage.setItem('user', JSON.stringify(userData));

          // fetch app context and log in
          appContextService.getAppContext().then((response) => {
            const context: AppContext = response.data.data;
            window.localStorage.setItem('app_context', JSON.stringify(context));
            const redirect = searchParams.get('redirect');
            setIsLoading(false);
            if (redirect) {
              setSearchParams({});
              navigate(redirect);
            } else {
              navigate('/home');
            }
          });
        })
        .catch((error: any) => {
          setIsLoading(false);
          const snackbarMessage =
            error?.response?.status === 401
              ? 'Error: username or password is incorrect'
              : 'There was an error logging in. Please try again later';

          showSnackbar(snackbarMessage, 'error');
        });
    }
  };

  const validate = (): boolean => {
    const errors: FieldErrors<LoginField> = {};
    if (email.length < 1) {
      errors.email = 'Email is required';
    } else if (isEmailInvalid(email)) {
      errors.email = 'Email is invalid';
    }
    if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailChange = (event: any) => {
    setEmail(event.target.value);
    clearFieldError('email');
  };

  const handlePasswordChange = (event: any) => {
    setPassword(event.target.value);
    clearFieldError('password');
  };

  const handlePasswordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  const divStyle = {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover', // Ensures the background image covers the entire div
    backgroundPosition: 'center', // Centers the background image
    backgroundRepeat: 'no-repeat', // Prevents the background image from repeating
  };

  return (
    <>
      <div className="login-header">
        <img src={logo} className="login-header-logo" />
      </div>
      <div className="login-body" style={divStyle}>
        <Card variant="outlined" style={{ backgroundColor: 'rgba(25, 24, 23, 0.85)' }}>
          <CardHeader
            style={{ paddingBottom: 0 }}
            title={
              <Typography variant="h3" textAlign={'center'} color={'white'}>
                Log In
              </Typography>
            }
          />
          <CardContent className="login-card-content">
            <Typography textOverflow={'wrap'} textAlign={'center'} color={'white'}>
              Log in with email
            </Typography>
            <Typography
              textOverflow="wrap"
              textAlign="center"
              color={palette.divider}
              fontSize={14}
              marginBottom={'16px'}
              marginTop={'8px'}
            >
              Don&apos;t have an account yet?{' '}
              <Link className="login-link" href="/create-account" color={palette.primary.light} underline="always">
                Sign up
              </Link>
            </Typography>
            <form className="login-form">
              {Object.keys(fieldErrors).length > 0 && (
                <Alert severity="error" style={{ marginBottom: 20 }}>
                  Please fix the highlighted fields below
                </Alert>
              )}
              <TextField
                id="login-field-email"
                type="text"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || ''}
                size="small"
                fullWidth
                sx={{ mt: 2, backgroundColor: 'white', borderRadius: 1, '& input': { textAlign: 'center' } }}
              />
              <TextField
                id="login-field-password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={handlePasswordKeyDown as any}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || ''}
                size="small"
                fullWidth
                sx={{ mt: 2, backgroundColor: 'white', borderRadius: 1, '& input': { textAlign: 'center' } }}
              />
            </form>
            <Button
              variant="contained"
              style={{ textTransform: 'none', marginTop: 32 }}
              disabled={isLoading}
              onClick={handleLogin}
            >
              Log In
            </Button>
            <Typography textOverflow="wrap" textAlign="center" color={palette.divider} fontSize={14} marginTop={'16px'}>
              Forgot your password?{' '}
              <Link className="login-link" href="/password-reset" color={palette.primary.light} underline="always">
                Click here
              </Link>
            </Typography>
            <Typography textOverflow="wrap" textAlign="center" color={palette.divider} fontSize={14} marginTop={'16px'}>
              v{process.env.REACT_APP_VERSION}
            </Typography>
          </CardContent>
        </Card>
        {/* <div>
            <ReCAPTCHA 
              ref={recaptchaRef}
              sitekey="YOUR_SITE_KEY"
              onChange={handleRecaptchaChange}
            />
        </div>*/}
      </div>
    </>
  );
};

export default LoginPage;
